import React, { useState } from 'react'
import produce from 'immer'
import { Config, Jetty, Quay } from 'src/types'
import ExpandedTable from 'src/components/base/ExpandedTable'
import Button from 'src/components/base/Button'
import { confirmDeletePopup } from 'src/components/popup/confirmDeletePopup'

import { INITIAL_JETTY } from './constants'
import JettyModal from './modals/JettyModal'

export type TPropsQuayConfigJettySection = {
  config: Quay
  setConfig: React.Dispatch<React.SetStateAction<Config>>
}

function QuayConfigJettySection({ config, setConfig }: TPropsQuayConfigJettySection) {
  const [selectedJetty, setSelectedJetty] = useState<(Jetty & { index: number }) | null>(null)

  const onAddJetty = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    setSelectedJetty({ ...INITIAL_JETTY, index: config.jetties.length })
  }

  const onRemoveJetty = (index: number, jetty: Jetty) => {
    confirmDeletePopup({
      itemName: jetty.name,
      onConfirm: () => {
        setConfig(
          produce((draft: Config) => {
            draft.quays.find(quay => quay.name === config.name)?.jetties.splice(index, 1)
          })
        )
      },
      alert: true
    })
  }

  return (
    <>
      <ExpandedTable emptyMessage="No jetties defined" empty={config?.jetties.length === 0}>
        <ExpandedTable.Header>
          <ExpandedTable.Title>Jetties</ExpandedTable.Title>
          <ExpandedTable.Actions>
            <Button variant="brand-tertiary" icon="fa fa-plus" onClick={onAddJetty}>
              Add jetty
            </Button>
          </ExpandedTable.Actions>
        </ExpandedTable.Header>
        <ExpandedTable.Body>
          <ExpandedTable.Row>
            <ExpandedTable.Cell asHeader style={{ width: '50%' }}>
              Jetty name
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
              Bollard
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
              Products
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '10%' }} />
          </ExpandedTable.Row>
          {config?.jetties.map((jetty, index) => {
            return (
              <ExpandedTable.Row key={index}>
                <ExpandedTable.Cell style={{ width: '50%' }}>{jetty.name}</ExpandedTable.Cell>
                <ExpandedTable.Cell style={{ width: '20%' }}>{jetty.bollard}</ExpandedTable.Cell>
                <ExpandedTable.Cell style={{ width: '20%' }}>
                  {jetty.products.map(p => p.product).join(', ')}
                </ExpandedTable.Cell>
                <ExpandedTable.Cell className="align-right" style={{ width: '10%' }}>
                  <ExpandedTable.CellActions>
                    <Button
                      onClick={() => setSelectedJetty({ ...jetty, index })}
                      size="small"
                      variant="neutral-tertiary"
                      icon="fa fa-edit"
                    />
                    <Button
                      onClick={() => {
                        onRemoveJetty(index, jetty)
                      }}
                      size="small"
                      variant="neutral-tertiary"
                      icon="fa fa-trash"
                    />
                  </ExpandedTable.CellActions>
                </ExpandedTable.Cell>
              </ExpandedTable.Row>
            )
          })}
        </ExpandedTable.Body>
      </ExpandedTable>
      {selectedJetty && (
        <JettyModal
          selectedJetty={selectedJetty}
          isOpen={!!selectedJetty}
          onJettyConfirm={jetty => {
            if (jetty) {
              setConfig(
                produce((draft: Config) => {
                  const quay = draft.quays.find(quay => quay.name === config.name)
                  const jettyIndex = selectedJetty.index
                  if (jettyIndex === -1) {
                    quay.jetties.push(jetty)
                  } else {
                    quay.jetties[jettyIndex] = jetty
                  }
                })
              )
            }
            setSelectedJetty(null)
          }}
          onCancel={() => setSelectedJetty(null)}
        />
      )}
    </>
  )
}

export default QuayConfigJettySection
